/**
 * QuickStartScreen3 - Session Logging
 *
 * Third screen of the Quick Start onboarding flow.
 * Allows users to log past play sessions either from collection dates
 * or manually by searching and selecting games with win/loss results.
 */

import React, { useState, useEffect } from "react";
import { API_BASE } from "../../config/api";
import GameSearchDropdown from "../common/GameSearchDropdown";

const SESSION_OPTIONS = {
  COLLECTION_DATES: "collection_dates",
  MANUAL: "manual",
};

const RESULT_OPTIONS = [
  { value: "win", label: "Win", className: "active-win" },
  { value: "loss", label: "Loss", className: "active-loss" },
];

const QuickStartScreen3 = ({
  sessionOption,
  setSessionOption,
  sessionGames,
  onAddGame,
  onRemoveGame,
  onUpdateResult,
  onCreateFromCollection,
  onBulkCreateSessions,
  onSkip,
  onBack,
  isLoading,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Search games when query changes
  useEffect(() => {
    const searchGames = async () => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await fetch(
          `${API_BASE}/games/search?q=${encodeURIComponent(searchQuery)}&limit=15`
        );
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.games || []);
        }
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchGames, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSelectGame = (game) => {
    onAddGame(game, "loss");
    setSearchQuery("");
    setShowDropdown(false);
  };

  return (
    <div className="session-screen">
      <div className="bgg-header">
        <h3>Log Your Play Sessions</h3>
        <p>
          Track the games you&apos;ve played. This helps Pista learn your preferences
          and provide better recommendations.
        </p>
      </div>

      {/* Session option selection */}
      <div className="session-options">
        <div
          className={`session-option-card ${
            sessionOption === SESSION_OPTIONS.COLLECTION_DATES ? "selected" : ""
          }`}
          onClick={() => setSessionOption(SESSION_OPTIONS.COLLECTION_DATES)}
        >
          <div className="session-option-icon">📅</div>
          <div className="session-option-title">Use Collection Dates</div>
          <div className="session-option-desc">
            Create sessions from when you added games to your collection
          </div>
          {sessionOption === SESSION_OPTIONS.COLLECTION_DATES && (
            <div className="session-option-info" style={{marginTop: "0.5rem", fontSize: "0.8rem", color: "var(--text-secondary, #666)", fontStyle: "italic"}}>
              First 20 sessions created immediately, rest created in background with notification when done.
            </div>
          )}
        </div>

        <div
          className={`session-option-card ${
            sessionOption === SESSION_OPTIONS.MANUAL ? "selected" : ""
          }`}
          onClick={() => setSessionOption(SESSION_OPTIONS.MANUAL)}
        >
          <div className="session-option-icon">✏️</div>
          <div className="session-option-title">Add Manually</div>
          <div className="session-option-desc">
            Search and select games you&apos;ve played recently
          </div>
        </div>
      </div>

      {/* Manual entry section */}
      {sessionOption === SESSION_OPTIONS.MANUAL && (
        <div className="session-games-section">
          <h4>Select Games You&apos;ve Played</h4>

          <GameSearchDropdown
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            isVisible={showDropdown}
            onVisibilityChange={setShowDropdown}
            searchResults={searchResults}
            isLoading={isSearching}
            onGameSelect={handleSelectGame}
            showImages={true}
            placeholder="Search for games you've played..."
          />

          <div className="selected-games-list" style={{ marginTop: "1rem" }}>
            {sessionGames.length === 0 ? (
              <div className="empty-selection">
                No sessions added yet.
                <br />
                Search and add games above.
              </div>
            ) : (
              sessionGames.map((game) => (
                <div key={game.id} className="selected-game-item">
                  <div className="selected-game-header">
                    {game.thumbnail && (
                      <img
                        src={game.thumbnail}
                        alt={game.name}
                        className="selected-game-thumb"
                      />
                    )}
                    <div className="selected-game-info">
                      <div className="selected-game-name">{game.name}</div>
                    </div>
                    <button
                      className="selected-game-remove"
                      onClick={() => onRemoveGame(game.id)}
                      aria-label={`Remove ${game.name}`}
                    >
                      ×
                    </button>
                  </div>

                  <div className="session-game-result">
                    <span style={{ fontSize: "0.875rem", color: "var(--text-secondary, #666)" }}>
                      Result:
                    </span>
                    {RESULT_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        className={`result-button ${
                          game.result === opt.value ? `active-${opt.value}` : ""
                        }`}
                        onClick={() => onUpdateResult(game.id, opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <div
                    style={{
                      fontSize: "0.8125rem",
                      color: "var(--text-secondary, #888)",
                      marginTop: "0.25rem",
                    }}
                  >
                    {game.result === "win"
                      ? "You marked this as a win"
                      : "System marked as winner (logged for stats)"}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="quick-start-footer" style={{ marginTop: "1.5rem" }}>
        <div className="quick-start-footer-left">
          <button
            className="btn btn-secondary"
            onClick={onBack}
            disabled={isLoading}
          >
            ← Back
          </button>
          <button
            className="btn btn-text"
            onClick={onSkip}
            disabled={isLoading}
          >
            Skip this step
          </button>
        </div>
        <div className="quick-start-footer-right">
          {sessionOption === SESSION_OPTIONS.COLLECTION_DATES && (
            <button
              className="btn btn-primary"
              onClick={onCreateFromCollection}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span
                    className="loading-spinner"
                    style={{ width: 16, height: 16, borderWidth: 2 }}
                  />
                  Creating...
                </>
              ) : (
                "Create Sessions from Collection →"
              )}
            </button>
          )}

          {sessionOption === SESSION_OPTIONS.MANUAL && (
            <button
              className="btn btn-primary"
              onClick={onBulkCreateSessions}
              disabled={sessionGames.length === 0 || isLoading}
            >
              {isLoading ? (
                <>
                  <span
                    className="loading-spinner"
                    style={{ width: 16, height: 16, borderWidth: 2 }}
                  />
                  Logging...
                </>
              ) : (
                `Log ${sessionGames.length} Session${sessionGames.length !== 1 ? "s" : ""} →`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickStartScreen3;
